import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/auth/AuthProvider';
import { maintenanceEnabled, useMaintenance } from '@/api/maintenance';

/**
 * Chrome for unauthenticated routes. Each auth page composes its own
 * full-viewport chrome via AuthShell, so this must NOT add padding/centering
 * (doing so would constrain AuthShell's full-screen gradient).
 *
 * Already-signed-in users are bounced to the app — except on flows that must
 * still run while authenticated: invitation acceptance, the SSO callback,
 * staff impersonation (identity switch), and the maintenance notice.
 */
function runsWhileAuthenticated(pathname: string): boolean {
  return (
    pathname.startsWith('/auth/invitation') ||
    pathname.startsWith('/auth/callback') ||
    pathname.startsWith('/auth/maintenance') ||
    pathname.startsWith('/auth/reconciliation') ||
    pathname.includes('impersonat')
  );
}

/**
 * Screens that stay reachable during a maintenance window: the notice itself, and the staff
 * entry points (impersonation / reconciliation links) so support can still verify a release.
 */
function openDuringMaintenance(pathname: string): boolean {
  return (
    pathname.startsWith('/auth/maintenance') ||
    pathname.startsWith('/auth/reconciliation') ||
    pathname.includes('impersonat')
  );
}

export function AuthLayout() {
  const { isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  const maintenance = useMaintenance();

  // Wait for the first read so a locked login form never flashes. Fail-open: a failed read
  // resolves to null/undefined and falls through.
  if (maintenanceEnabled && maintenance.isPending && !openDuringMaintenance(pathname)) {
    return null;
  }
  if (maintenance.data?.maintenance && !openDuringMaintenance(pathname)) {
    return <Navigate to="/auth/maintenance" replace />;
  }

  if (isAuthenticated && !runsWhileAuthenticated(pathname)) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}
